import { useEffect, useRef, useState } from 'react';
import type { WorkOrderDto } from '@signalvote/shared';
import { ErrorCode, assertNever } from '@signalvote/shared';
import { ApiError } from '@signalvote/api-client';
import { useRefetchWorkOrder } from '@/hooks/queries';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { voterDataCountPromise, voterDataRecordsLabel } from './voterDataOrder';
import { Confidence } from './voterEstimate';
import type { Estimate } from './voterEstimate';
import { formatDollars } from './voterPurpose';
import { WorkOrderPaymentPanel, useWorkOrderPayment } from './WorkOrderPayment';
import type { CardPayment } from './WorkOrderPayment';

const FALLBACK_RETRY_SECONDS = 60;
/** Past this age a Draft's quote is a stored one, which Pay checks again. */
const STAND_IN_MINUTES = 10;

export enum CheckoutStep {
  /** Asking the server for a fresh quote. */
  Quoting = 'Quoting',
  /** L2 is busy and no recent count could stand in: the request is sent again after the wait. */
  Waiting = 'Waiting',
  /** A quoted Draft, waiting for the customer to pay at its price. */
  Confirming = 'Confirming',
  /** The server refused the quote, such as a list under the minimum order. */
  Refused = 'Refused',
}

export type Checkout =
  | { step: CheckoutStep.Quoting }
  | {
      step: CheckoutStep.Waiting;
      retryAfterSeconds: number;
      /** The Draft whose payment waits, so the retry pays the same one; null while the quote itself waits. */
      draft: WorkOrderDto | null;
    }
  | {
      step: CheckoutStep.Confirming;
      draft: WorkOrderDto;
      /** The price the customer last confirmed, when the fresh quote at Pay came back different. */
      changedFromCents: number | null;
    }
  | { step: CheckoutStep.Refused; message: string };

/**
 * Buying a Voter data list on its own: `start` places a Draft at a fresh quote, or `confirm` shows an
 * existing one, and `payment` pays it the way a Text send is paid: Pay checks the Draft out at the
 * price on screen, then takes the card on that invoice. The server re-quotes at every checkout and
 * refuses a price that moved; the step then shows the new price, and Pay needs it confirmed. While
 * L2 is busy, the quote or the checkout is sent again after the server's wait.
 */
export function useVoterDataCheckout() {
  const [checkout, setCheckout] = useState<Checkout | null>(null);
  const generation = useRef(0);
  const quoting = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const refetch = useRefetchWorkOrder();
  const payAgain = useRef<() => void>(() => {});

  useEffect(() => () => clearTimeout(timer.current), []);

  const draft =
    checkout?.step === CheckoutStep.Confirming || checkout?.step === CheckoutStep.Waiting ? (checkout.draft ?? undefined) : undefined;
  const payment = useWorkOrderPayment({
    workOrder: draft,
    totalCents: draft?.voterData?.priceCents ?? null,
    confirmedVoterDataPriceCents: draft?.voterData?.priceCents ?? null,
    onRefused: (error) => {
      const run = generation.current;
      if (!draft) return;
      const confirmedPriceCents = draft.voterData!.priceCents;
      const again = () => {
        setCheckout({ step: CheckoutStep.Confirming, draft, changedFromCents: null });
        payAgain.current();
      };
      if (waitThen(run, error, draft, again)) return;
      // The server saved the fresh quote on the Draft before refusing: show it, and pay only once it is confirmed.
      if (error instanceof ApiError && error.code === ErrorCode.VoterDataPriceChanged) {
        ask(run, () => refetch(draft.id), confirmedPriceCents);
      }
    },
  });
  payAgain.current = () => payment.pay();

  const waitThen = (run: number, error: unknown, waitingDraft: WorkOrderDto | null, again: () => void): boolean => {
    if (!(error instanceof ApiError && error.status === 429)) return false;
    const retryAfterSeconds = error.retryAfterSeconds ?? FALLBACK_RETRY_SECONDS;
    setCheckout({ step: CheckoutStep.Waiting, retryAfterSeconds, draft: waitingDraft });
    timer.current = setTimeout(() => generation.current === run && again(), retryAfterSeconds * 1000);
    return true;
  };

  const ask = (run: number, quote: () => Promise<WorkOrderDto>, changedFromCents: number | null = null) => {
    quoting.current = true;
    setCheckout({ step: CheckoutStep.Quoting });
    quote().then(
      (quoted) => {
        if (generation.current !== run) return;
        quoting.current = false;
        setCheckout({ step: CheckoutStep.Confirming, draft: quoted, changedFromCents });
      },
      (error: unknown) => {
        if (generation.current !== run || waitThen(run, error, null, () => ask(run, quote, changedFromCents))) return;
        quoting.current = false;
        setCheckout({
          step: CheckoutStep.Refused,
          message: error instanceof ApiError && error.message ? error.message : 'Couldn’t check the price. Please try again.',
        });
      },
    );
  };

  const begin = () => {
    clearTimeout(timer.current);
    return ++generation.current;
  };

  const start = (quote: () => Promise<WorkOrderDto>) => {
    if (!quoting.current) ask(begin(), quote);
  };

  const confirm = (quoted: WorkOrderDto) => {
    begin();
    setCheckout({ step: CheckoutStep.Confirming, draft: quoted, changedFromCents: null });
  };

  const close = () => {
    generation.current++;
    quoting.current = false;
    clearTimeout(timer.current);
    setCheckout(null);
  };

  const busy = checkout?.step === CheckoutStep.Quoting || checkout?.step === CheckoutStep.Waiting;
  return { checkout, start, confirm, close, busy, payment };
}

/**
 * The confirm step before paying for a Voter data list: the count, households and price of a
 * quoted Draft, how far the delivered file may differ, and, when it applies, that the price changed
 * since the customer confirmed it, that it differs from the builder's estimate, or how old a count
 * that stood in while L2 was busy is. Nothing is charged until Pay, and a changed price is paid only
 * through a button that names it. Once Pay raises the invoice, the card form takes the payment here.
 */
export function VoterDataConfirm({
  checkout,
  estimate,
  payment,
  onClose,
}: {
  checkout: Checkout | null;
  /** The builder's price on screen before the exact quote, when the customer came from the builder. */
  estimate: Estimate | null;
  payment: CardPayment;
  onClose: () => void;
}) {
  const confirming = checkout?.step === CheckoutStep.Confirming ? checkout : null;
  const draft = confirming?.draft ?? null;
  const quote = draft?.voterData ?? null;
  const exact = quote ? formatDollars(quote.priceCents) : null;
  const changedFromCents = confirming?.changedFromCents ?? null;
  const priceChanged = quote !== null && changedFromCents !== null && changedFromCents !== quote.priceCents;
  const ageMinutes = quote ? countAgeMinutes(quote.countedAt) : 0;
  // The invoice is raised: the card form carries the payment from here, its errors included.
  const cardUp = payment.stage !== 'idle' && payment.stage !== 'starting';
  const { action } = payment;
  const label = action.busy ? action.busyLabel : priceChanged && payment.stage === 'idle' ? `Confirm ${exact} and pay` : action.label;

  return (
    <Dialog open={checkout !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm your order</DialogTitle>
          <DialogDescription>Check the exact count and price. Nothing is charged until you pay.</DialogDescription>
        </DialogHeader>

        {checkout && <QuoteBody checkout={checkout} />}

        {quote && (
          <div className="voter-quote-in space-y-3 text-sm">
            {quote.sentence && <p className="text-base font-medium leading-snug">{quote.sentence}</p>}
            <div className="flex items-baseline justify-between gap-4">
              <div>
                <p className="tabular-nums">{`${quote.quotedCount.toLocaleString()} voters in ${quote.quotedHouseholds.toLocaleString()} households`}</p>
                <p className="text-muted-foreground tabular-nums">You pay for {voterDataRecordsLabel(quote.records, quote.onePerHousehold)}.</p>
              </div>
              <p className="text-lg font-semibold tabular-nums">{exact}</p>
            </div>
            {priceChanged ? (
              <p role="status" className="rounded-md bg-amber-50 p-2 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200">
                The price changed from {formatDollars(changedFromCents)} to {exact}. Nothing was charged.
              </p>
            ) : (
              estimate &&
              differs(estimate, quote.priceCents) && (
                <p role="status" className="rounded-md bg-amber-50 p-2 text-amber-900 dark:bg-amber-500/15 dark:text-amber-200">
                  The exact price is {exact}. The estimate was {priceText(estimate)}.
                </p>
              )
            )}
            {ageMinutes > STAND_IN_MINUTES ? (
              <p className="text-muted-foreground">The price is checked again when you pay.</p>
            ) : (
              quote.countedWhileBusy &&
              ageMinutes > 0 && (
                <p className="text-muted-foreground">Counted {plural(ageMinutes, 'minute')} ago, while live counts were busy.</p>
              )
            )}
            <p className="text-xs text-muted-foreground">{voterDataCountPromise(quote.records, quote.onePerHousehold)}</p>
          </div>
        )}

        {quote && cardUp && <WorkOrderPaymentPanel payment={payment} />}

        {draft && !cardUp && !priceChanged && payment.error && (
          <p role="alert" className="text-sm text-destructive">
            {payment.error}
          </p>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Not now
          </Button>
          <Button onClick={action.onPress} disabled={!draft || action.disabled || action.busy}>
            {label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuoteBody({ checkout }: { checkout: Checkout }) {
  switch (checkout.step) {
    case CheckoutStep.Quoting:
      return (
        <p role="status" className="text-sm text-muted-foreground">
          Getting the exact price…
        </p>
      );
    case CheckoutStep.Waiting:
      return (
        <p role="status" className="text-sm text-muted-foreground">
          Exact price in about {checkout.retryAfterSeconds} s.
        </p>
      );
    case CheckoutStep.Refused:
      return (
        <p role="alert" className="text-sm text-destructive">
          {checkout.message}
        </p>
      );
    case CheckoutStep.Confirming:
      return null;
    default:
      return assertNever(checkout);
  }
}

function differs(earlier: Estimate, exactCents: number): boolean {
  switch (earlier.confidence) {
    case Confidence.Exact:
    case Confidence.About:
      return Math.round(earlier.value) !== exactCents;
    case Confidence.Range:
      return exactCents < earlier.low || exactCents > earlier.high;
    default:
      return assertNever(earlier);
  }
}

function priceText(estimate: Estimate): string {
  switch (estimate.confidence) {
    case Confidence.Exact:
      return formatDollars(estimate.value);
    case Confidence.About:
      return `about ${formatDollars(estimate.value, true)}`;
    case Confidence.Range:
      return `${formatDollars(estimate.low, true)}–${formatDollars(estimate.high, true)}`;
    default:
      return assertNever(estimate);
  }
}

function countAgeMinutes(countedAt: string | null): number {
  return countedAt ? Math.floor((Date.now() - Date.parse(countedAt)) / 60_000) : 0;
}

function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}
